import React, { useState, useEffect } from "react";
import {
  Building2,
  Plus,
  Edit,
  Trash2,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Settings,
  Check,
  X,
} from "lucide-react";
import LoadingSpinner from "../components/common/LoadingSpinner";
import {
  getBranches,
  createBranch,
  updateBranch,
  deleteBranch,
} from "../services/branches";
import {
  createAcademicTerm,
  activateAcademicTerm,
} from "../services/academicTerms";
import {
  Branch,
  CreateBranchRequest,
  UpdateBranchRequest,
} from "../types/branch";
import { toast } from "react-hot-toast";

const BranchManagement: React.FC = () => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);

  // Semester management state
  const [showSemesterModal, setShowSemesterModal] = useState(false);
  const [semesterBranch, setSemesterBranch] = useState<Branch | null>(null);
  const [semesterFormData, setSemesterFormData] = useState({
    name: "",
    startDate: "",
    endDate: "",
  });
  const [semesterLoading, setSemesterLoading] = useState(false);

  const [formData, setFormData] = useState<CreateBranchRequest>({
    name: "",
    abbreviation: "",
    address: {
      street: "",
      city: "",
      state: "",
      zipCode: "",
      country: "Kenya",
    },
    contactInfo: {
      phone: "",
      email: "",
      website: "",
    },
    configuration: {
      currency: "KES",
      receiptPrefix: "RCP",
      studentIdPrefix: "STU",
      teacherIdPrefix: "TCH",
      timezone: "Africa/Nairobi",
      academicYear: new Date().getFullYear().toString(),
    },
  });

  const fetchBranches = async () => {
    try {
      setLoading(true);
      const response = await getBranches();
      setBranches(response.data || []);
    } catch (error) {
      console.error("Failed to fetch branches:", error);
      toast.error("Failed to load branches");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  const handleCreateBranch = () => {
    setModalMode("create");
    setSelectedBranch(null);
    setFormData({
      name: "",
      abbreviation: "",
      address: {
        street: "",
        city: "",
        state: "",
        zipCode: "",
        country: "Kenya",
      },
      contactInfo: {
        phone: "",
        email: "",
        website: "",
      },
      configuration: {
        currency: "KES",
        receiptPrefix: "RCP",
        studentIdPrefix: "STU",
        teacherIdPrefix: "TCH",
        timezone: "Africa/Nairobi",
        academicYear: new Date().getFullYear().toString(),
      },
    });
    setShowModal(true);
  };

  const handleEditBranch = (branch: Branch) => {
    setModalMode("edit");
    setSelectedBranch(branch);
    setFormData({
      name: branch.name,
      abbreviation: branch.abbreviation || "",
      address: branch.address || {
        street: "",
        city: "",
        state: "",
        zipCode: "",
        country: "Kenya",
      },
      contactInfo: branch.contactInfo || {
        phone: "",
        email: "",
        website: "",
      },
      configuration: branch.configuration || {
        currency: "KES",
        receiptPrefix: "RCP",
        studentIdPrefix: "STU",
        teacherIdPrefix: "TCH",
        timezone: "Africa/Nairobi",
        academicYear: new Date().getFullYear().toString(),
      },
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (modalMode === "create") {
        await createBranch(formData);
        toast.success("Branch created successfully");
      } else if (selectedBranch) {
        await updateBranch(selectedBranch._id, formData as UpdateBranchRequest);
        toast.success("Branch updated successfully");
      }
      setShowModal(false);
      fetchBranches();
    } catch (error) {
      console.error("Failed to save branch:", error);
      toast.error(`Failed to ${modalMode} branch`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteBranch = async (branchId: string) => {
    if (window.confirm("Are you sure you want to delete this branch?")) {
      try {
        await deleteBranch(branchId);
        toast.success("Branch deleted successfully");
        fetchBranches();
      } catch (error) {
        console.error("Failed to delete branch:", error);
        toast.error("Failed to delete branch");
      }
    }
  };

  // Semester management functions
  const handleManageSemesters = (branch: Branch) => {
    setSemesterBranch(branch);
    setShowSemesterModal(true);
    setSemesterFormData({ name: "", startDate: "", endDate: "" });
  };

  const handleCreateSemester = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!semesterBranch) return;

    // Validate dates
    if (
      new Date(semesterFormData.startDate) >= new Date(semesterFormData.endDate)
    ) {
      toast.error("Start date must be before end date");
      return;
    }

    try {
      setSemesterLoading(true);
      await createAcademicTerm(semesterBranch._id, semesterFormData);
      toast.success("Academic term created successfully");
      setSemesterFormData({ name: "", startDate: "", endDate: "" });
      // Refresh the branches to get updated academic terms
      await fetchBranches();
    } catch (error: any) {
      console.error("Failed to create academic term:", error);
      toast.error(
        error.response?.data?.message || "Failed to create academic term"
      );
    } finally {
      setSemesterLoading(false);
    }
  };

  const handleActivateSemester = async (termId: string) => {
    if (!semesterBranch) return;

    try {
      setSemesterLoading(true);
      await activateAcademicTerm(semesterBranch._id, termId);
      toast.success("Academic term activated successfully");
      // Refresh the branches to get updated academic terms
      await fetchBranches();
    } catch (error: any) {
      console.error("Failed to activate academic term:", error);
      toast.error(
        error.response?.data?.message || "Failed to activate academic term"
      );
    } finally {
      setSemesterLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const filteredBranches = branches.filter(
    (branch) =>
      branch.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      branch.address?.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      branch.contactInfo?.email
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  const getAddressString = (address: Branch["address"]) => {
    if (!address) return "";
    const parts = [address.street, address.city, address.state].filter(Boolean);
    return parts.join(", ");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-8">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-slate-200 border-t-blue-500 rounded-full animate-spin"></div>
            <p className="text-slate-600 font-semibold">Loading branches...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-4 shadow-lg">
            <Building2 className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">
              Branch Management
            </h1>
            <p className="text-slate-600 font-semibold">
              Manage all branches in the system
            </p>
          </div>
        </div>
        <button
          onClick={handleCreateBranch}
          className="bg-white border-2 border-slate-200 rounded-xl px-6 py-3 font-bold text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-3 shadow-md"
        >
          <Plus className="w-5 h-5 text-blue-500" />
          <span>Add Branch</span>
        </button>
      </div>

      {/* Search */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-4">
        <input
          type="text"
          placeholder="Search branches by name, city, or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
        />
      </div>

      {/* Branches Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredBranches.map((branch) => (
          <div
            key={branch._id}
            className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300"
          >
            <div className="flex justify-between items-start mb-6">
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-3 shadow-lg">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-lg">
                    {branch.name}
                  </h3>
                  <span
                    className={`inline-block px-3 py-1.5 rounded-xl text-xs font-bold shadow-md mt-1 ${
                      branch.status === "active"
                        ? "bg-gradient-to-r from-emerald-300 to-green-400 text-white"
                        : branch.status === "inactive"
                        ? "bg-gradient-to-r from-red-300 to-red-400 text-white"
                        : "bg-gradient-to-r from-amber-300 to-orange-400 text-white"
                    }`}
                  >
                    {branch.status.charAt(0).toUpperCase() +
                      branch.status.slice(1)}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEditBranch(branch)}
                  className="bg-white border-2 border-slate-200 rounded-xl p-2 hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  <Edit className="w-4 h-4 text-blue-500" />
                </button>
                <button
                  onClick={() => handleDeleteBranch(branch._id)}
                  className="bg-white border-2 border-slate-200 rounded-xl p-2 hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>
            </div>

            <div className="space-y-3 mb-4">
              {branch.address && getAddressString(branch.address) && (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200/60">
                  <MapPin className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  <span className="text-sm font-semibold text-slate-900">
                    {getAddressString(branch.address)}
                  </span>
                </div>
              )}
              {branch.contactInfo?.phone && (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200/60">
                  <Phone className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span className="text-sm font-semibold text-slate-900">
                    {branch.contactInfo.phone}
                  </span>
                </div>
              )}
              {branch.contactInfo?.email && (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200/60">
                  <Mail className="w-4 h-4 text-purple-500 flex-shrink-0" />
                  <span className="text-sm font-semibold text-slate-900">
                    {branch.contactInfo.email}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200/60">
                <Calendar className="w-4 h-4 text-amber-500 flex-shrink-0" />
                <span className="text-sm font-semibold text-slate-900">
                  Created: {new Date(branch.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-200">
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="text-center p-3 rounded-xl bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200/60">
                  <div className="text-sm font-bold text-slate-900 mb-1">
                    Currency
                  </div>
                  <div className="text-lg font-bold text-blue-600">
                    {branch.configuration?.currency || "N/A"}
                  </div>
                </div>
                <div className="text-center p-3 rounded-xl bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200/60">
                  <div className="text-sm font-bold text-slate-900 mb-1">
                    Prefix
                  </div>
                  <div className="text-lg font-bold text-purple-600">
                    {branch.configuration?.receiptPrefix || "N/A"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredBranches.length === 0 && (
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-amber-300 to-orange-400 rounded-2xl shadow-lg mb-4">
              <Building2 className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              No branches found
            </h3>
            <p className="text-slate-600 font-semibold mb-6">
              {searchTerm
                ? "No branches match your search criteria"
                : "Get started by creating your first branch"}
            </p>
            {!searchTerm && (
              <button
                onClick={handleCreateBranch}
                className="bg-white border-2 border-slate-200 rounded-xl px-6 py-3 font-bold text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300 inline-flex items-center gap-3 shadow-md"
              >
                <Plus className="w-5 h-5 text-blue-500" />
                Create Branch
              </button>
            )}
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-200 p-6 rounded-t-2xl">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-3 shadow-lg">
                  <Building2 className="h-6 w-6 text-white" />
                </div>
                {modalMode === "create" ? "Create Branch" : "Edit Branch"}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  Branch Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  Branch Abbreviation *
                </label>
                <input
                  type="text"
                  required
                  value={formData.abbreviation}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      abbreviation: e.target.value.toUpperCase(),
                    })
                  }
                  className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                  placeholder="e.g., NAI, MOMB"
                />
              </div>

              {/* Address Fields */}
              <div className="space-y-4">
                <h3 className="text-base font-bold text-slate-900">Address</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-slate-900 mb-2">
                      Street
                    </label>
                    <input
                      type="text"
                      value={formData.address?.street || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          address: {
                            ...formData.address,
                            street: e.target.value,
                          },
                        })
                      }
                      className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-900 mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      value={formData.address?.city || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          address: {
                            ...formData.address,
                            city: e.target.value,
                          },
                        })
                      }
                      className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-900 mb-2">
                      State
                    </label>
                    <input
                      type="text"
                      value={formData.address?.state || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          address: {
                            ...formData.address,
                            state: e.target.value,
                          },
                        })
                      }
                      className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-900 mb-2">
                      Zip Code
                    </label>
                    <input
                      type="text"
                      value={formData.address?.zipCode || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          address: {
                            ...formData.address,
                            zipCode: e.target.value,
                          },
                        })
                      }
                      className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-4">
                <h3 className="text-base font-bold text-slate-900">
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-slate-900 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.contactInfo?.phone || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          contactInfo: {
                            ...formData.contactInfo,
                            phone: e.target.value,
                          },
                        })
                      }
                      className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-900 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.contactInfo?.email || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          contactInfo: {
                            ...formData.contactInfo,
                            email: e.target.value,
                          },
                        })
                      }
                      className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
              </div>

              {/* Configuration */}
              <div className="space-y-4">
                <h3 className="text-base font-bold text-slate-900">
                  Configuration
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-slate-900 mb-2">
                      Currency
                    </label>
                    <select
                      value={formData.configuration?.currency || "KES"}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          configuration: {
                            ...formData.configuration,
                            currency: e.target.value,
                          },
                        })
                      }
                      className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                    >
                      <option value="KES">KES</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-900 mb-2">
                      Receipt Prefix
                    </label>
                    <input
                      type="text"
                      value={formData.configuration?.receiptPrefix || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          configuration: {
                            ...formData.configuration,
                            receiptPrefix: e.target.value,
                          },
                        })
                      }
                      className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="bg-white border-2 border-slate-200 rounded-xl px-6 py-3 font-bold text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl px-6 py-3 font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Check className="w-5 h-5" />
                  {isSubmitting
                    ? "Saving..."
                    : modalMode === "create"
                    ? "Create"
                    : "Update"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Semester Management Modal */}
      {showSemesterModal && semesterBranch && (
        <div className="fixed inset-0 z-[9999] overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity z-[9998]"></div>
            <div className="relative bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-2xl w-full max-w-2xl z-[10000]">
              <div className="p-6">
                {/* Modal Header */}
                <div className="flex justify-between items-center mb-6 pb-4 border-b border-slate-200">
                  <div className="flex items-center gap-4">
                    <div className="bg-gradient-to-br from-indigo-300 to-purple-400 rounded-2xl p-3 shadow-lg">
                      <Settings className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900">
                        Manage Semesters - {semesterBranch.name}
                      </h3>
                      <p className="text-sm text-slate-600 font-semibold">
                        Create and manage academic terms for this branch
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowSemesterModal(false)}
                    className="bg-white border-2 border-slate-200 rounded-xl p-2 hover:shadow-lg hover:scale-105 transition-all duration-300"
                  >
                    <X className="h-5 w-5 text-slate-700" />
                  </button>
                </div>

                {/* Create New Semester Form */}
                <div className="mb-6 p-6 bg-gradient-to-r from-slate-50 to-blue-50 rounded-2xl border border-slate-200/60">
                  <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3">
                    <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-xl p-2 shadow-md">
                      <Plus className="h-5 w-5 text-white" />
                    </div>
                    Create New Academic Term
                  </h4>
                  <form onSubmit={handleCreateSemester} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-slate-900 mb-2">
                          Term Name *
                        </label>
                        <input
                          type="text"
                          value={semesterFormData.name}
                          onChange={(e) =>
                            setSemesterFormData({
                              ...semesterFormData,
                              name: e.target.value,
                            })
                          }
                          className="w-full px-4 py-3 bg-white border-2 border-slate-200/60 rounded-xl font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                          placeholder="e.g., Fall 2025, Term 1"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-slate-900 mb-2">
                          Start Date *
                        </label>
                        <input
                          type="date"
                          value={semesterFormData.startDate}
                          onChange={(e) =>
                            setSemesterFormData({
                              ...semesterFormData,
                              startDate: e.target.value,
                            })
                          }
                          className="w-full px-4 py-3 bg-white border-2 border-slate-200/60 rounded-xl font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-slate-900 mb-2">
                          End Date *
                        </label>
                        <input
                          type="date"
                          value={semesterFormData.endDate}
                          onChange={(e) =>
                            setSemesterFormData({
                              ...semesterFormData,
                              endDate: e.target.value,
                            })
                          }
                          className="w-full px-4 py-3 bg-white border-2 border-slate-200/60 rounded-xl font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                          required
                        />
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={semesterLoading}
                        className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-bold"
                      >
                        {semesterLoading ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <Plus className="w-5 h-5" />
                        )}
                        {semesterLoading ? "Creating..." : "Create Term"}
                      </button>
                    </div>
                  </form>
                </div>

                {/* Existing Academic Terms */}
                <div>
                  <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3">
                    <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-xl p-2 shadow-md">
                      <Calendar className="h-5 w-5 text-white" />
                    </div>
                    Existing Academic Terms (
                    {semesterBranch.academicTerms?.length || 0})
                  </h4>

                  {semesterBranch.academicTerms &&
                  semesterBranch.academicTerms.length > 0 ? (
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {semesterBranch.academicTerms.map((term: any) => (
                        <div
                          key={term._id}
                          className={`border-2 rounded-xl p-4 transition-all duration-300 ${
                            term.isActive
                              ? "border-emerald-300 bg-gradient-to-r from-emerald-50 to-green-50 shadow-md"
                              : "border-slate-200 bg-white/95 backdrop-blur-xl hover:shadow-lg"
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h5 className="font-bold text-slate-900 text-lg">
                                  {term.name}
                                </h5>
                                {term.isActive && (
                                  <span className="bg-gradient-to-r from-emerald-300 to-green-400 text-white text-xs px-3 py-1.5 rounded-xl font-bold shadow-md">
                                    Active
                                  </span>
                                )}
                              </div>
                              <div className="text-sm font-semibold text-slate-600 space-y-1">
                                <p className="flex items-center gap-2">
                                  <span className="text-blue-500">Start:</span>
                                  {formatDate(term.startDate)}
                                </p>
                                <p className="flex items-center gap-2">
                                  <span className="text-purple-500">End:</span>
                                  {formatDate(term.endDate)}
                                </p>
                              </div>
                            </div>
                            {!term.isActive && (
                              <button
                                onClick={() => handleActivateSemester(term._id)}
                                disabled={semesterLoading}
                                className="bg-white border-2 border-emerald-300 rounded-xl p-3 hover:bg-gradient-to-r hover:from-emerald-50 hover:to-green-50 hover:shadow-lg hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Activate Term"
                              >
                                <Check className="h-5 w-5 text-emerald-500" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-gradient-to-r from-slate-50 to-blue-50 rounded-2xl border-2 border-dashed border-slate-300">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-slate-300 to-slate-400 rounded-2xl shadow-lg mb-3">
                        <Calendar className="w-8 h-8 text-white" />
                      </div>
                      <p className="text-slate-900 font-bold mb-1">
                        No academic terms created yet
                      </p>
                      <p className="text-sm text-slate-600 font-semibold">
                        Create your first academic term above
                      </p>
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="flex justify-end pt-6 mt-6 border-t border-slate-200">
                  <button
                    onClick={() => setShowSemesterModal(false)}
                    className="bg-white border-2 border-slate-200 rounded-xl px-6 py-3 font-bold text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BranchManagement;
